/**
 * QaEnvironmentBanner — faixa global de ambiente QA.
 *
 * Detecção por HOSTNAME em runtime, não por env de build: a imagem de QA é o
 * MESMO `vite build` de produção (import.meta.env.MODE é 'production' nos
 * dois), então nada vindo do build distingue os ambientes. O hostname é a
 * única diferença garantida entre os deploys
 * (`<cliente>.qa.trademarketingforce.com` vs `<cliente>.trademarketingforce.com`)
 * e não depende de env/args de build chegarem intactos ao container.
 *
 * ⚠️ TMF9 é multi-tenant: produção tem subdomínio por cliente
 * (`apex.trademarketingforce.com`). A regex só dispara quando um LABEL do
 * host é exatamente `qa` — nunca quando "qa" aparece dentro de outro label.
 *
 * Portado da implementação de referência do HelpCor (HCOR-63222/HCOR-63233).
 *
 * @since 2026-08-26
 */

import { useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import { TriangleAlert } from 'lucide-react';
// ⚠️ Import do módulo CONCRETO de tokens, não do barrel '@/theme': a faixa
// vive FORA do ThemeProvider (App a monta antes de qualquer provider) e o
// barrel é mockado inteiro por testes de outras áreas (ex.:
// ThemeAuthSync.test.tsx faz vi.mock('@/theme') sem coreFeedback, o que
// derrubava a suite no CI). Tokens puros não têm contexto — importar direto
// é coerente com o desenho "não depende de tema" [TMF9-63228].
import { coreFeedback, coreGray } from '@/theme/designTokens';

/**
 * Retorna true quando o hostname pertence a um ambiente de QA.
 * Um label do host precisa ser exatamente "qa" (ex.: `apex.qa.exemplo.com`,
 * `qa.exemplo.com`); "qa" no meio de um label não conta (`pesquisaqa.exemplo.com`).
 */
export function isQaEnvironment(hostname: string = window.location.hostname): boolean {
  return /(^|\.)qa\./i.test(hostname);
}

/**
 * [TMF9-63228] Formata o `restored_at` do marcador de restore para exibição
 * (DD/MM/AAAA, pt-BR). Exportada para teste; devolve `null` para entrada
 * ausente/inválida — a faixa simplesmente omite a data (produção e QA sem
 * marcador caem aqui).
 */
export function formatRestoreDate(isoString: string | null | undefined): string | null {
  if (!isoString) return null;
  const d = new Date(isoString);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

// Amarelo/warning canônico do design system (coreFeedback.warning) com texto
// quase-preto: contraste alto e idêntico nos dois temas de propósito — a
// faixa é um aviso de ambiente, não deve mudar de cara com o tema.
const bannerStyle: CSSProperties = {
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  zIndex: 2000,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  padding: '4px 12px',
  backgroundColor: coreFeedback.warning.base,
  color: coreGray[950],
  font: '700 12px system-ui, sans-serif',
  letterSpacing: '.04em',
  textTransform: 'uppercase',
  borderTop: `1px solid ${coreFeedback.warning.dark}`,
};

interface QaEnvironmentBannerProps {
  /** Override do hostname — só para teste; em produção usa window.location. */
  hostname?: string;
}

export function QaEnvironmentBanner({ hostname }: QaEnvironmentBannerProps = {}) {
  const active = isQaEnvironment(hostname ?? window.location.hostname);
  // [TMF9-63228] Data do último restore do banco de QA, gravada pela rotina
  // de restore e servida por GET /api/v1/qa/data-info (público, sem PHI —
  // metadado de AMBIENTE, tenant-agnóstico). Fetch direto (sem apiClient) e
  // relativo à origem: a faixa é pré-login e não deve passar por
  // interceptors de auth. Fail-silent: qualquer falha (backend antigo,
  // marcador ausente, rede) apenas omite a data — nunca quebra a faixa.
  const [restoreDate, setRestoreDate] = useState<string | null>(null);

  useEffect(() => {
    if (!active) return undefined;
    const controller = new AbortController();
    fetch('/api/v1/qa/data-info', { signal: controller.signal })
      .then((r) => (r.ok ? r.json() : null))
      .then((info) => setRestoreDate(formatRestoreDate(info?.restored_at)))
      .catch(() => {});
    return () => controller.abort();
  }, [active]);

  if (!active) return null;

  return (
    <div style={bannerStyle} role="status" aria-label="Ambiente de QA">
      <TriangleAlert size={14} aria-hidden="true" />
      <span>
        Ambiente de QA — dados de teste, não use dados reais
        {restoreDate ? ` · dados restaurados em ${restoreDate}` : ''}
      </span>
    </div>
  );
}

export default QaEnvironmentBanner;
